<template>
  <div class="apple-equalizer">
    <AppleSettingRow title="启用均衡器" :description="equalizer.message" @activate="toggleFromLabel">
      <template #default="row"><AppleSwitch :aria-labelledby="row.labelId" :aria-describedby="row.descriptionId" :value="equalizer.enabled" :disabled="toggling" @change="toggle" /></template>
    </AppleSettingRow>
    <p v-if="preference.state.error" class="apple-eq-error" role="status">{{ preference.state.error }}</p>
    <div class="apple-eq-layout">
      <div class="apple-eq-copy"><strong>预设音效</strong><small>{{ presetDescription }}</small></div>
      <div class="apple-eq-preset-control"><AppleSegmentedControl aria-label="预设音效" :value="selectedPreset" :options="presetOptions" :disabled="!equalizer.enabled" @input="selectPreset" /></div>
      <div class="apple-eq-copy"><strong>均衡器</strong><small>调整各频段的增益，打造个性化音效</small></div>
      <div class="apple-eq-controls">
        <div class="apple-eq-scale" aria-hidden="true"><span>+12 dB</span><span>0 dB</span><span>−12 dB</span></div>
        <div class="apple-eq-bands" aria-label="均衡器频段">
          <div v-for="(frequency, index) in frequencies" :key="frequency" class="apple-eq-band">
            <input :id="'apple-eq-' + index" type="range" min="-12" max="12" step="1" :value="equalizer.gains[index]" :disabled="!equalizer.enabled" :aria-label="frequency + ' Hz 增益'" :aria-valuetext="gainText(equalizer.gains[index])" :style="bandStyle(equalizer.gains[index])" @input="band(index, $event.target.value)" />
            <label :for="'apple-eq-' + index">{{ frequency >= 1000 ? (frequency / 1000) + 'k' : frequency }}</label>
          </div>
        </div>
        <AppleButton variant="subtle" :disabled="!equalizer.enabled" @click="preset('flat')">重置</AppleButton>
      </div>
    </div>
  </div>
</template>

<script>
import EqualizerSettings from '../salt/EqualizerSettings.vue';
import AppleSettingRow from './ui/AppleSettingRow.vue';
import AppleSwitch from './ui/AppleSwitch.vue';
import AppleSegmentedControl from './ui/AppleSegmentedControl.vue';
import AppleButton from './ui/AppleButton.vue';

// Reuse the existing equalizer state and setters; this component only changes its presentation.
export default {
  name: 'AppleEqualizerSettings',
  extends: EqualizerSettings,
  components: { AppleSettingRow, AppleSwitch, AppleSegmentedControl, AppleButton },
  computed: {
    selectedPreset() { return ['flat','bass','vocal'].includes(this.equalizer.preset) ? this.equalizer.preset : 'custom'; },
    presetDescription() { return (this.equalizer.enabled ? (this.equalizer.active ? '正在处理当前音频' : '当前歌曲使用原音播放') : '启用均衡器后可调整音效') + (this.equalizer.preset === 'treble' ? ' · 明亮曲线' : ''); },
    presetOptions() {
      return [
        { value: 'flat', label: '原声' },
        { value: 'bass', label: this.presets.bass.label },
        { value: 'vocal', label: this.presets.vocal.label },
        { value: 'custom', label: '自定义' },
      ];
    },
  },
  methods: {
    toggleFromLabel() { if (!this.toggling) this.toggle(!this.equalizer.enabled); },
    selectPreset(value) {
      // Selecting custom preserves the current curve; the shared setter marks it custom.
      if (value === 'custom') this.band(0, this.equalizer.gains[0]);
      else this.preset(value);
    },
    gainText(value) { return `${value > 0 ? '+' : ''}${value} dB`; },
    bandStyle(value) { return { '--apple-eq-fill': `${(Number(value) + 12) / 24 * 100}%` }; },
  },
};
</script>

<style scoped>
.apple-eq-layout { border-top:1px solid var(--apple-ui-border,var(--apple-border)); }
.apple-eq-error { margin:0; padding:0 0 8px; color:var(--apple-ui-error,#c42b1c); font-size:12px; }
[data-theme='dark'] .apple-eq-error { color:var(--apple-ui-error,#ff7b86); }
.apple-eq-layout { display:grid; grid-template-columns:minmax(150px,.6fr) minmax(0,1fr); align-items:start; gap:10px 18px; padding:10px 0; }
.apple-eq-copy { min-width:0; font-size:13px; line-height:1.4; }
.apple-eq-copy strong { display:block; font-weight:500; }
.apple-eq-copy small { display:block; margin-top:2px; color:var(--apple-ui-muted,var(--color-secondary)); font-size:12px; line-height:1.45; }
.apple-eq-preset-control { min-width:0; padding-left:0; }
.apple-eq-preset-control .apple-segmented-control { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:5px; width:100%; }
.apple-eq-preset-control ::v-deep .apple-segmented-option { min-width:0; padding-inline:5px; font-size:12px; }
.apple-eq-controls { display:grid; grid-template-columns:30px minmax(0,1fr) 50px; align-items:center; gap:10px; min-width:0; }
.apple-eq-scale { align-self:stretch; display:flex; flex-direction:column; justify-content:space-between; padding:3px 0 23px; color:var(--apple-ui-muted,var(--color-secondary)); font-size:10px; white-space:nowrap; }
.apple-eq-bands { display:grid; grid-template-columns:repeat(10,minmax(0,1fr)); min-width:0; gap:3px; }
.apple-eq-band { display:flex; align-items:center; flex-direction:column; gap:4px; min-width:0; color:var(--apple-ui-muted,var(--color-secondary)); font-size:10px; }
.apple-eq-band input { box-sizing:border-box; width:24px; height:72px; margin:0; padding:0; appearance:none; writing-mode:vertical-lr; direction:rtl; background:transparent; cursor:pointer; }
.apple-eq-band input::-webkit-slider-runnable-track { width:4px; border-radius:3px; background:linear-gradient(to top,var(--apple-ui-accent,var(--color-primary)) var(--apple-eq-fill),color-mix(in srgb,var(--apple-ui-text,var(--color-text)) 18%,transparent) var(--apple-eq-fill)); }
.apple-eq-band input::-webkit-slider-thumb { width:14px; height:14px; margin-left:-5px; appearance:none; border:1px solid color-mix(in srgb,var(--apple-ui-text,var(--color-text)) 14%,transparent); border-radius:50%; background:var(--apple-ui-surface-raised,#fff); box-shadow:0 1px 3px #0003; }
.apple-eq-band input::-moz-range-track { width:4px; border-radius:3px; background:color-mix(in srgb,var(--apple-ui-text,var(--color-text)) 18%,transparent); }
.apple-eq-band input::-moz-range-progress { width:4px; border-radius:3px; background:var(--apple-ui-accent,var(--color-primary)); }
.apple-eq-band input::-moz-range-thumb { width:14px; height:14px; border:1px solid color-mix(in srgb,var(--apple-ui-text,var(--color-text)) 14%,transparent); border-radius:50%; background:var(--apple-ui-surface-raised,#fff); }
.apple-eq-band input:focus:not(:focus-visible) { outline:none; }
.apple-eq-band input:focus-visible { outline:2px solid var(--apple-ui-focus,var(--apple-ui-accent)); outline-offset:-2px; border-radius:6px; }
.apple-eq-band input:disabled { opacity:.45; cursor:default; }
@media (max-width:760px) { .apple-eq-layout { grid-template-columns:minmax(0,1fr); gap:8px; } .apple-eq-copy:not(:first-child) { margin-top:8px; } }
</style>
